import { ROLE_CODE } from '~/core/constants/role'
import type { IColorBadgeType } from '~/core/ui/Badge'
import type { LucideIconName } from '~/core/ui/IconWrapper'
import type { ISelectOption } from '~/core/ui/Select'

export enum JOB_STATUS_ENUM {
  publish = 'publish',
  internal = 'internal',
  archived = 'archived',
  draft = 'draft'
}
export const JOB_NOTE_TAB = 'notes'

export const DEFAULT_SELECTED_JOB_STATUS_ROLE_MAP: Record<string, JOB_STATUS_ENUM[]> = {
  default: [JOB_STATUS_ENUM.draft, JOB_STATUS_ENUM.internal, JOB_STATUS_ENUM.publish],
  defaultNoDraft: [JOB_STATUS_ENUM.internal, JOB_STATUS_ENUM.publish],
  [ROLE_CODE.LimitedMember]: [JOB_STATUS_ENUM.internal, JOB_STATUS_ENUM.publish],
  [ROLE_CODE.Admin]: [JOB_STATUS_ENUM.draft, JOB_STATUS_ENUM.internal, JOB_STATUS_ENUM.publish],
  [ROLE_CODE.Member]: [JOB_STATUS_ENUM.draft, JOB_STATUS_ENUM.internal, JOB_STATUS_ENUM.publish]
}

export const JOB_DOT_STATUS = (status: string) => {
  return (
    {
      [JOB_STATUS_ENUM.internal]: 'purple',
      [JOB_STATUS_ENUM.publish]: 'green',
      [JOB_STATUS_ENUM.archived]: 'red',
      [JOB_STATUS_ENUM.draft]: 'gray'
    }[status] || 'gray'
  )
}

export const JOB_COLOR_STAGE_NAME = (stageTypes: string) => {
  return (
    {
      sourced: 'blue',
      applied: 'purple',
      screening: 'red',
      'client-submission': 'orange',
      clientSubmission: 'orange',
      submission: 'orange',
      interview: 'yellow',
      offer: 'pastelGreen',
      hired: 'green',
      onboard: 'green',
      probation: 'green'
    }[stageTypes] || 'gray'
  )
}

export const JOB_APPLICANT_STATUS = {
  rejected: 'rejected',
  inprocessing: 'inprocessing'
} as const

export const JOB_STAGE_GROUP = {
  hires: 'hires',
  applicants: 'applicants',
  activeProcess: 'active_process'
} as const

interface JobBoardItem {
  code: string
  label: string
  icon: string
  w: number
  h: number
  tooltip: string
}

export const LISTS_PUBLISH_JOB_BOARDS: JobBoardItem[] = [
  {
    code: 'google_for_jobs',
    label: 'Google for jobs',
    icon: '/img/feature/jobBoards/logoGG.svg',
    w: 80,
    h: 26,
    tooltip:
      'Have your job postings found by millions of job seekers who search on Google every day.'
  }
]

export const PLACE_VIEW_PIPELINE_ENUM = {
  jobDetail: 'jobDetail',
  jobList: 'jobList'
} as const

export const SOURCE_REFERRAL_REWARD = [
  {
    value: 'money'
  },
  {
    value: 'orther_reward'
  }
] as const

export const REFERRAL_REWARD_MONEY_VALUE = 'money'
export const REFERRAL_REWARD_OTHER_VALUE = 'orther_reward'

export const JOB_STATUS_OPTION_ROLE_MAP = ({
  currentRoleCode,
  jobStatus = [],
  allowedStatusValues
}: {
  currentRoleCode: string
  jobStatus: ISelectOption[]
  /** Giá trị status được phép (sau lọc role), vd. publish, internal */
  allowedStatusValues?: string[]
}): ISelectOption[] => {
  let mappings: string[] = []
  if (ROLE_CODE.Admin === currentRoleCode) {
    mappings = [
      JOB_STATUS_ENUM.publish,
      JOB_STATUS_ENUM.internal,
      JOB_STATUS_ENUM.archived,
      JOB_STATUS_ENUM.draft
    ]
  }

  if (ROLE_CODE.Member === currentRoleCode) {
    mappings = [
      JOB_STATUS_ENUM.publish,
      JOB_STATUS_ENUM.internal,
      JOB_STATUS_ENUM.archived,
      JOB_STATUS_ENUM.draft
    ]
  }

  if (ROLE_CODE.LimitedMember === currentRoleCode) {
    mappings = [JOB_STATUS_ENUM.publish, JOB_STATUS_ENUM.internal, JOB_STATUS_ENUM.archived]
  }

  if (allowedStatusValues?.length) {
    mappings = mappings.filter((value) => allowedStatusValues.includes(value))
  }

  return jobStatus?.filter((status) => mappings.includes(status.value))
}
export const MATCHED_RANK_BADGE_COLOR: Record<string, IColorBadgeType> = {
  S: 'matched-rank-s',
  A: 'matched-rank-a',
  B: 'matched-rank-b',
  C: 'matched-rank-c'
}

export const SAVE_JOB_FILTER = [
  {
    value: 'all_jobs',
    supportingObj: {
      name: 'all_jobs_text'
    }
  },
  {
    value: 'saved',
    supportingObj: {
      name: 'saved_text'
    }
  },
  {
    value: 'unsaved',
    supportingObj: {
      name: 'unsaved_text'
    }
  }
] as const

export const SYSTEM_JOB_FIELDS = {
  referenceId: 'reference_id',
  remote_status: 'remote_status',
  salary: 'salary',
  employment_type: 'employment_type',
  job_level: 'job_level',
  job_talent_pool_ids: 'job_talent_pool_ids',
  tags: 'tags',
  public_id: 'public_id',
  company: 'company',
  locations: 'locations',
  education: 'education',
  skills: 'skills',
  languages: 'languages',
  pitch: 'pitch',
  jobCategory: 'job_category',
  department: 'department',
  numberOfHeadcount: 'number_of_headcount',
  archivedAt: 'archived_at',
  openedAt: 'opened_at',
  applicationDeadlineAt: 'application_deadline_at'
} as const

export const SYSTEM_JOB_FIELD_BY_DISPLAY_KEY: Record<string, string> = {
  referenceId: SYSTEM_JOB_FIELDS.referenceId,
  education: SYSTEM_JOB_FIELDS.education,
  skills: SYSTEM_JOB_FIELDS.skills,
  languages: SYSTEM_JOB_FIELDS.languages,
  pitch: SYSTEM_JOB_FIELDS.pitch,
  jobCategory: SYSTEM_JOB_FIELDS.jobCategory,
  department: SYSTEM_JOB_FIELDS.department,
  numberOfHeadcount: SYSTEM_JOB_FIELDS.numberOfHeadcount
}

export const TONE_LIST = [
  {
    value: 'formal',
    supportingObj: {
      name: 'formal_tone'
    }
  },
  {
    value: 'friendly',
    supportingObj: {
      name: 'friendly_tone'
    }
  },
  {
    value: 'engaging',
    supportingObj: {
      name: 'engaging_tone'
    }
  }
] as const

export const TABS_KEY_MATCHING_CONDITION = {
  job: 'job',
  profile: 'profile'
} as const

export const DUE_DATE_OPEN_JOB = 30

export const JOB_NEW_YES_NO: ISelectOption[] = [{ value: 'yes' }, { value: 'no' }]

export const FIELDS_USER_SETTING_DISPLAY: {
  [key: string]: { key: string; iconMenus: LucideIconName }
} = {
  publicId: {
    key: 'publicId',
    iconMenus: 'ClipboardList'
  },
  title: {
    key: 'title',
    iconMenus: 'Briefcase'
  },
  description: {
    key: 'description',
    iconMenus: 'AlignLeft'
  },
  status: {
    key: 'status',
    iconMenus: 'CheckSquare'
  },
  stages: {
    key: 'stages',
    iconMenus: 'SplitSquareHorizontal'
  },
  company: {
    key: 'company',
    iconMenus: 'Building'
  },
  locations: {
    key: 'locations',
    iconMenus: 'MapPin'
  },
  department: {
    key: 'department',
    iconMenus: 'Server'
  },
  tags: {
    key: 'tags',
    iconMenus: 'Tag'
  },
  numberOfHeadcount: {
    key: 'numberOfHeadcount',
    iconMenus: 'User'
  },
  hiringTeams: {
    key: 'hiringTeams',
    iconMenus: 'Users'
  },
  views: {
    key: 'views',
    iconMenus: 'Eye'
  },
  closingDate: {
    key: 'closingDate',
    iconMenus: 'CalendarClock'
  },
  createdAt: {
    key: 'createdAt',
    iconMenus: 'Clock'
  },
  createdBy: {
    key: 'createdBy',
    iconMenus: 'UserPlus'
  },
  updatedAt: {
    key: 'updatedAt',
    iconMenus: 'Clock'
  },
  openedAt: {
    key: 'openedAt',
    iconMenus: 'Clock'
  },
  pitch: {
    key: 'pitch',
    iconMenus: 'AlignLeft'
  },
  jobLevel: {
    key: 'jobLevel',
    iconMenus: 'Zap'
  },
  education: {
    key: 'education',
    iconMenus: 'BookOpen'
  },
  skills: {
    key: 'skills',
    iconMenus: 'StretchHorizontal'
  },
  languages: {
    key: 'languages',
    iconMenus: 'Languages'
  },
  remoteStatus: {
    key: 'remoteStatus',
    iconMenus: 'Wifi'
  },
  jobCategory: {
    key: 'jobCategory',
    iconMenus: 'Table'
  },
  employmentType: {
    key: 'employmentType',
    iconMenus: 'Briefcase'
  },
  salary: {
    key: 'salary',
    iconMenus: 'DollarSign'
  },
  jobTalentPoolIds: {
    key: 'jobTalentPoolIds',
    iconMenus: 'FolderSearch'
  },
  externalApplicationUrl: {
    key: 'externalApplicationUrl',
    iconMenus: 'ClipboardSignatureIcon'
  }
}

export enum NOTE_TAB_GROUP {
  MEMBER = 'member',
  JOB_ROLE = 'job_role',
  DEPARTMENT = 'department'
}

export const JOB_YES_NO: ISelectOption[] = [{ value: 'yes' }, { value: 'no' }]

export const OPERATOR = {
  and: 'and',
  or: 'or'
}

export const FILTER_JOB_FIELDS_VALUE = {
  operator: 'operator',
  public_id: 'public_id',
  title: 'title',
  description: 'description',
  reference_id: 'reference_id',
  status: 'status',
  list_saved_job_member_ids: 'list_saved_job_member_ids', // saved status
  country_state_id: 'country_state_id',
  department_id: 'department_id',
  headcount: 'headcount',
  hiring_member_ids: 'hiring_member_ids',
  job_category: 'job_category',
  opened_at: 'opened_at',
  created_at: 'created_at',
  updated_at: 'updated_at',
  created_by: 'created_by',
  employment_type: 'employment_type',
  tagged_ids: 'tagged_ids',
  company_id: 'company_id',
  closing_date: 'closing_date',
  tabsMembersTeams: 'tabsMembersTeams',
  pitch: 'pitch',
  jobLevel: 'job_level',
  education: 'education',
  skills: 'skills',
  languages: 'languages',
  remoteStatus: 'remote_status',
  jobTalentPoolIds: 'job_talent_pool_ids',
  external_application_url: 'external_application_url',
  salary: 'salary',
  companyType: 'company_kind'
}

export const FILTER_JOB_FIELDS_CONDITION = {
  equal: 'equal',
  greaterThan: 'greater_than',
  lessThan: 'less_than',
  contains: 'contains',
  is: 'is',
  isNot: 'is_not',
  isAnyOf: 'is_any_of',
  isNoneOf: 'is_none_of',
  isEmpty: 'is_empty',
  isNotEmpty: 'is_not_empty',
  range: 'range',
  anyOfAll: 'any_of_all',
  anyOf: 'any_of',
  all: 'all',
  and: 'and',
  or: 'or'
}

export enum JOB_ACTION {
  VIEW = 0,
  DELETE = 1,
  EDIT = 2,
  CHANGE_STATUS = 3,
  DUPLICATE_JOB = 4,
  SHARE_JOB = 5
}

export const HUMAN_INTERVIEW = 'human'
export const AI_INTERVIEW = 'artificial_intelligence'

export enum ModalType {
  candidate = 'candidate',
  callLog = 'callLog',
  contact = 'contact',
  company = 'company',
  interview = 'interview'
}
export const DURATION_INTERVIEW = [
  {
    value: '15',
    supportingObj: {
      name: '15Minutes'
    }
  },
  {
    value: '30',
    supportingObj: {
      name: '30Minutes'
    }
  },
  {
    value: '45',
    supportingObj: {
      name: '45Minutes'
    }
  },
  {
    value: '60',
    supportingObj: {
      name: '60Minutes'
    }
  },
  {
    value: '90',
    supportingObj: {
      name: '90Minutes'
    }
  },
  {
    value: '120',
    supportingObj: {
      name: '120Minutes'
    }
  }
]

export const JOB_VIEW_DISPLAY_STATE = [{ value: 'public' }, { value: 'private' }]
export const COMPANY_KIND_FILTER = [
  {
    value: 'internal',
    supportingObj: {
      name: 'internal'
    }
  },
  {
    value: 'external',
    supportingObj: {
      name: 'external'
    }
  }
]

export const JOB_SCHEDULE_TIME_DEFAULT = '09:00'
